import {HttpErrorResponse} from '@angular/common/http';
import {Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {FabTableState, filterStateChange, process} from '@fabric/components/table-filter/data-query';
import {catchError, forkJoin, map, Observable, ObservableInput, of} from 'rxjs';
import {ErrorCodes} from '../../constants/ErrorCodes';
import {UserFunctionCode} from '../../constants/UserFunctionCode';
import {DeactivateGuardedComponent} from '../../interfaces/DeactivateGuardedComponent';
import {MaintenanceUser} from '../../models/MaintenanceUser';
import {MaintenanceUserRole} from '../../models/MaintenanceUserRole';
import {NotificationService} from '../../services/notification.service';
import {SecurityService} from '../../services/security.service';
import {SolicitanteService} from '../../services/solicitante.service';
import {UserService} from '../../services/user.service';
import {
  FabDateFilterOperator,
  RemDateFilterComponent
} from '../../shared-components/table-filter/date-filter/date-filter.component';
import {RemNumberFilterComponent} from '../../shared-components/table-filter/number-filter/number-filter.component';
import {
  FabStringFilterOperator,
  RemStringFilterComponent
} from '../../shared-components/table-filter/string-filter/string-filter.component';

class UserRow extends MaintenanceUser {
  public add: boolean;
  public editing: boolean = false;
  public loading: boolean = false;
  public failNames: boolean = false;

  private _originalUser: MaintenanceUser;

  public set originalUser(user: MaintenanceUser) {
    this._originalUser = user;
    this.id = user.id;
    this.resetEdits();
  }

  public get roleName(): string {
    return this.role.role;
  }

  public get originalUser(): MaintenanceUser {
    return this._originalUser;
  }

  constructor(add: boolean = false, user?: MaintenanceUser) {
    super();
    this.role = new MaintenanceUserRole();
    this._originalUser = user ?? new MaintenanceUser();
    this.id = user?.id ?? 0;
    this.add = add;
    this.editing = add;
    this.resetEdits();
  }

  public resetEdits() {
    this.gmin = this.originalUser.gmin;
    this.nombre = this.originalUser.nombre;
    this.apellido = this.originalUser.apellido;
    this.estado = this.originalUser.estado;
    this.role.id = this.originalUser.role.id;
    this.role.role = this.originalUser.role.role;
    this.usrCreacion = this.originalUser.usrCreacion;
    this.fecCreacion = new Date(this.originalUser.fecCreacion);
    this.usrModif = this.originalUser.usrModif;
    this.fecModif = this.originalUser.fecModif;
  }
}

@Component({
  selector: 'app-user-maintenance',
  templateUrl: './user-maintenance.component.html',
  styleUrls: ['./user-maintenance.component.scss']
})
export class UserMaintenanceComponent implements OnInit, DeactivateGuardedComponent {
  isLoading: boolean = true;
  displayedColumns: string[] = ['gmin', 'nombre', 'apellido', 'role', 'estado', 'usrCreacion', 'fecCreacion', 'usrModif', 'fecModif'];
  operators: FabStringFilterOperator[] = ['contains'];
  dateOperators: FabDateFilterOperator[] = ['eq', 'gte', 'lte'];
  pageSizeOptions: number[] = [10, 15, 20, 25];
  defaultPageSize: number = this.pageSizeOptions[0];
  dataSource: MatTableDataSource<UserRow> = new MatTableDataSource<UserRow>();
  dateFormat: string = 'dd/MM/yyyy';

  state: FabTableState = {
    skip: 0,
    take: 10,
    filter: [],
    sort: []
  };

  userRoles: MaintenanceUserRole[] | undefined = [];
  users: UserRow[] = [];

  canCreateUpdateUser: boolean = false;
  selector: string;

  constructor(readonly userService: UserService,
              private notificationService: NotificationService,
              private securityService: SecurityService,
              private solicitanteService: SolicitanteService,
              private elementRef: ElementRef) {
  }

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'role':
          return item.role.role;
        default: // @ts-ignore
          return item[property];
      }
    };
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  @ViewChildren('tableFilter') tableFilters: QueryList<RemStringFilterComponent | RemNumberFilterComponent | RemDateFilterComponent>;

  @HostListener('window:beforeunload', ['$event'])
  defaultConfirmation($event: any) {
    if (this.dirtyCheck()) {
      $event.returnValue(false);
    } else {
      $event.preventDefault();
    }
  }

  public get disableSaveButton() {
    return !this.validateAllRows() ||
      this.users.filter(element => element.editing).length == 0;
  }

  private dirtyCheck(): boolean {
    return !this.users.every((row) => {
      return !row.editing;
    })
  }

  ngOnInit(): void {
    this.canCreateUpdateUser = this.securityService.can(UserFunctionCode.CREATE_UPDATE_USER_ASSIGN_ROLE);
    if (this.canCreateUpdateUser) {
      this.displayedColumns.push('edited');
    }
    this.fetchData();
    this.selector = this.elementRef.nativeElement.tagName.toLowerCase();
  }

  filterChange(event: any) {
    this.state.skip = 0;
    if (this.matPaginator) {
      this.matPaginator.pageIndex = 0;
    }
    // @ts-ignore
    this.state.filter = filterStateChange(event, this.state.filter);
    this.processData();
  }

  fetchData() {
    this.isLoading = true;
    this.userService.getRoles().subscribe(res => {
      if(this.securityService.can(UserFunctionCode.VEHICLE_ROLES)){
        res.forEach((item) => {
          if(item.desc  == 'Vehicle' || item.desc == 'Both'){
            // @ts-ignore
            this.userRoles.push(item);
          }
        });
      } else {
        res.forEach((item) => {
          if (item.desc == 'Parts' || item.desc == 'Both') {
            // @ts-ignore
            this.userRoles.push(item);
          }
        });
      }
    });
    this.userService.getUsers().subscribe(res => {
      this.users = res.map((user) => {
        return new UserRow(false, user);
      });
      this.sortTableByRecentFirst();
      this.dataSource = new MatTableDataSource<UserRow>(this.users);
      this.processData();
      this.isLoading = false;
    })
  }

  processData() {
    this.dataSource.data = process(this.users, this.state);
  }

  addRow() {
    let newUser = new UserRow(true);
    newUser.usrCreacion = this.securityService.LOGGED_USER['gmin'];
    this.users.unshift(newUser);
    this.processData();
  }

  saveRows() {
    if (this.validateAllRows()) {

      this.checkSolicitantesExist().subscribe((existsResponse) => {
        if (existsResponse.every((item) => !item.exists) ||
          confirm(existsResponse.filter((item) => item.exists)
          .map((item) => (item.lastName + ", " + item.firstName))
          .join("; ") + " está creado como solicitante, por favor verifique.")
        ) {
          let observables: ObservableInput<any>[] = [];
          let errors: HttpErrorResponse[] = [];
          let saved = 0;
          this.users.filter((item) => item.editing)
          .forEach((user) => {
              if (user.add || this.isDirtyRow(user)) {
                observables.push(
                  this.userService.addUpdateUser(user, user.add)
                  .pipe(
                    map((res) => {
                      user.originalUser = res;
                      user.editing = false;
                      user.add = false;
                      saved++;
                    }),
                    catchError((err: HttpErrorResponse) => {
                      errors.push(err);
                      return of(null);
                    })
                  )
                );
              } else {
                this.revertRow(user);
              }
            }
          );

          forkJoin(observables).subscribe({
            next: undefined,
            error: undefined,
            complete: () => {
              if (errors.length == 0) {
                this.notificationService.open(ErrorCodes.SAVED, 'success');
              } else {
                const errorCounts = errors.map(err => err.error?.exceptionType ?? "Unknown").reduce((accumulator, value) => {
                  // @ts-ignore
                  return {...accumulator, [value]: (accumulator[value] || 0) + 1};
                }, {});
                let errorMessage = `${saved} línea(s) guardada(s). ${errors.length} líena(s) tiene(n) error:`
                .concat(...Object.entries(errorCounts).map(([msg, count]) => `\n${this.notificationService.getErrorByCode(msg)} (${count})`));
                this.notificationService.open(errorMessage, 'error');
              }
              this.sortTableByRecentFirst();
            }
          });

        } else {
          this.notificationService.open(ErrorCodes.NOT_SAVED, 'warn');
        }

      });

    } else {
      // invalid rows error
      this.notificationService.open(ErrorCodes.INVALID_DATA, 'error');
    }
  }

  private validateRow(user: UserRow) {
    return /^([0-9]{9})$/.test(user.gmin) &&
      user.nombre.length > 0 &&
      user.nombre.length <= 20 &&
      user.apellido.length > 0 &&
      user.apellido.length <= 20 &&
      this.userRoles?.find(role => role.id == user.role.id);
  }

  public validateAllRows() {
    return this.users.filter(element => element.editing).every((row) => this.validateRow(row));
  }

  edit(element: UserRow) {
    if (!element.editing) {
      element.usrModif = this.securityService.LOGGED_USER['gmin'];

      element.editing = true;
    }
  }

  onRoleChange(element: MaintenanceUserRole) {
    // @ts-ignore
    element.role = this.userRoles.find((role) => {
      return role.id == element.id;
    }).role
  }

  revertRow(row: UserRow) {
    if (row.add) {
      this.users = this.users.filter((item) => {
        return item !== row;
      })
    }
    row.resetEdits();
    row.editing = false;
    this.processData();
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.dirtyCheck()) {
      return confirm("Usted tiene cambios no guardados. ¿Salir de la página de todos modos?");
    } else {
      return true;
    }
  }

  onGminChange(element: UserRow) {
    element.failNames = false;
    element.loading = true;
    element.nombre = '';
    element.apellido = '';
    if (element.gmin.length == 9) {
      this.userService.getUserNames(element.gmin).subscribe({
        next: (response) => {
          element.nombre = response.firstName;
          element.apellido = response.lastName;
          element.loading = false;
        },
        error: err => {
          console.log('HTTP Error', err)
          element.failNames = true;
          element.loading = false;
        }
      });
    }
  }

  checkSolicitantesExist(): Observable<{ firstName: string, lastName: string, exists: boolean }[]> {
    let obs = [of({firstName: '', lastName: '', exists: false})];
    if (!this.securityService.can(UserFunctionCode.VEHICLE_ROLES)) {
      obs.push(...this.users.filter((item) => item.add)
      .map((user) => {
        return this.solicitanteService.exists(user.nombre, user.apellido).pipe(
          map((res) => {
            return {firstName: user.nombre, lastName: user.apellido, exists: res};
          }))
      }));
    }
    return forkJoin(obs);
  }

  sortTableByRecentFirst() {
    this.users.sort((a, b) => {
      let dateA = a.fecModif != undefined ? a.fecModif : a.fecCreacion;
      let dateB = b.fecModif != undefined ? b.fecModif : b.fecCreacion;
      // @ts-ignore
      return dateB - dateA;
    });
  }

  isDirtyRow(row: UserRow): boolean {
    return row.estado !== row.originalUser.estado || row.role.id !== row.originalUser.role.id;
  }
}
